import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useFamilyStore, useDomainStore } from '@/hooks/useStores';
import {
  AddFamilyMemberType,
  ParentChildRelationshipType,
  RelationshipType,
  type LivingStatusType
} from 'we-tree-api-client/v2';
import { parseDateOnly, toCanonicalBirthDate, formatDateForInput } from '@/utils/dateFormatting';
import type { FamilyMemberOption } from '@/components/family/FamilyMemberTypeahead';
import AddFamilyMemberCreateNewFields from './AddFamilyMemberCreateNewFields';
import AddFamilyMemberLinkExistingFields from './AddFamilyMemberLinkExistingFields';
import type { ParentChildRelationshipTypeValue } from '@/types/parent-child-relationship';
import type { AddFlowLocationState } from '@/types/add-flow-navigation';
import type { DatePrecision } from '@/utils/dateFormatting';
import { navigateCancelFromAddFlow } from '@/utils/navigateCancelFromAddFlow';
import PlanUpgradeAlert from '@/components/shared/PlanUpgradeAlert';
import { getUpgradeAwareErrorParts, isFamilyOwner } from '@/utils/familyPlan';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';

import '@/components/person/person-edit-form.css';
import '@/components/shared/add-flow-entry-toggle.css';
import './add-family-member-screen.css';

function hasExistingParentChildLink(
  familyMember1Id: string,
  familyMember2Id: string,
  relationships: Iterable<{ isParentChild: boolean; familyMember1Id: string; familyMember2Id: string }>
): boolean {
  for (const rel of relationships) {
    if (rel.isParentChild && rel.familyMember1Id === familyMember1Id && rel.familyMember2Id === familyMember2Id) {
      return true;
    }
  }
  return false;
}

function hasExistingSpousalLink(
  a: string,
  b: string,
  relationships: Iterable<{ relationshipType: string; familyMember1Id: string; familyMember2Id: string }>
): boolean {
  for (const rel of relationships) {
    if (rel.relationshipType === 'spousal') {
      if (
        (rel.familyMember1Id === a && rel.familyMember2Id === b) ||
        (rel.familyMember1Id === b && rel.familyMember2Id === a)
      ) {
        return true;
      }
    }
  }
  return false;
}

function getSubmitLabel(
  addSelf: boolean,
  entryMode: 'create' | 'link',
  isSpouse: boolean,
  isSubmitting: boolean
): string {
  if (isSubmitting) return 'Saving…';
  if (addSelf) return 'Add myself';
  if (entryMode === 'link') return isSpouse ? 'Link spouse' : 'Link';
  return 'Save';
}

const AddFamilyMemberScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as AddFlowLocationState | undefined;
  const familyStore = useFamilyStore();
  const domainStore = useDomainStore();

  const addSelf = searchParams.get('addSelf') === 'true';
  const parentFamilyMemberId = searchParams.get('parentId') ?? '';
  const childFamilyMemberId = searchParams.get('childId') ?? '';
  const relationshipType = (searchParams.get('type') ?? 'child') as 'child' | 'spouse' | 'parent';
  const isChild = relationshipType === 'child';
  const isSpouse = relationshipType === 'spouse';
  const isParent = relationshipType === 'parent';

  const entryMode = searchParams.get('entry') === 'link' ? 'link' : 'create';
  const setEntryMode = (mode: 'create' | 'link') => {
    const next = new URLSearchParams(searchParams);
    if (mode === 'link') next.set('entry', 'link');
    else next.delete('entry');
    setSearchParams(next, { replace: true });
  };

  const [linkOtherMemberId, setLinkOtherMemberId] = useState('');
  const [linkPcRelationshipType, setLinkPcRelationshipType] = useState<ParentChildRelationshipTypeValue>(
    ParentChildRelationshipType.Biological
  );

  const parentPerson = familyIdParam && parentFamilyMemberId ? familyStore.getPerson(parentFamilyMemberId) : null;
  const spouseIds = parentPerson?.spouseFamilyMemberIds ?? [];

  const [additionalParentChoices, setAdditionalParentChoices] = useState<
    Record<string, { selected: boolean; relationshipType: ParentChildRelationshipTypeValue }>
  >({});

  const [formData, setFormData] = useState({
    parentChildRelationshipType: ParentChildRelationshipType.Biological,
    givenName: '',
    middleName: '',
    surname: '',
    suffix: '',
    preferredName: '',
    gender: 'unknown' as const,
    birthDate: '',
    birthDatePrecision: 'day' as DatePrecision,
    birthPlace: '',
    deathDate: '',
    deathDatePrecision: 'day' as DatePrecision,
    deathPlace: '',
    isDeceased: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [upgradeHint, setUpgradeHint] = useState<string | null>(null);

  const isOwner =
    Boolean(familyIdParam) &&
    domainStore.selectedFamilyId === familyIdParam &&
    isFamilyOwner(domainStore.currentFamily?.userRole);

  const anchorFamilyMemberId = isParent ? childFamilyMemberId : parentFamilyMemberId;

  const petIds = new Set(familyStore.pets.keys());
  const linkExcludedIds = new Set<string>([anchorFamilyMemberId, ...petIds]);
  if (isChild) {
    familyStore.getChildRelationships(parentFamilyMemberId).forEach(({ child }) => {
      linkExcludedIds.add(child.familyMemberId);
    });
  } else if (isParent) {
    familyStore.getParentRelationships(childFamilyMemberId).forEach(({ parent }) => {
      linkExcludedIds.add(parent.familyMemberId);
    });
  } else if (isSpouse) {
    const anchor = familyStore.getPerson(parentFamilyMemberId);
    anchor?.spouseFamilyMemberIds.forEach((id) => linkExcludedIds.add(id));
  }

  const linkMemberOptions: FamilyMemberOption[] = Array.from(familyStore.people.values())
    .filter((p) => !p.isPlaceholder && !linkExcludedIds.has(p.familyMemberId))
    .map((p) => ({
      familyMemberId: p.familyMemberId,
      fullName: p.fullName,
      age: p.age,
      birthYear: p.birthDate ? String(new Date(p.birthDate).getFullYear()) : null,
      spouseName: null
    }));

  const submitLinkExisting = async () => {
    if (!familyIdParam || !linkOtherMemberId) {
      setSubmitError(t('addFlow.selectMemberToLink'));
      return;
    }
    if (linkOtherMemberId === anchorFamilyMemberId) {
      setSubmitError(t('addFlow.chooseSomeoneElse'));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (isSpouse) {
        if (hasExistingSpousalLink(parentFamilyMemberId, linkOtherMemberId, familyStore.relationships.values())) {
          setSubmitError(t('addFlow.alreadySpouses'));
          return;
        }
        const result = await familyStore.createRelationship({
          familyId: familyIdParam,
          familyMember1Id: parentFamilyMemberId,
          familyMember2Id: linkOtherMemberId,
          relationshipType: RelationshipType.Spousal
        });
        if (!result.success) {
          setSubmitError(result.error?.getUserMessage?.() ?? result.error?.message ?? t('addFlow.couldNotLinkSpouse'));
          return;
        }
      } else {
        const parentId = isChild ? parentFamilyMemberId : linkOtherMemberId;
        const childId = isChild ? linkOtherMemberId : childFamilyMemberId;
        if (hasExistingParentChildLink(parentId, childId, familyStore.relationships.values())) {
          setSubmitError(t('addFlow.parentChildExists'));
          return;
        }
        const result = await familyStore.createRelationship({
          familyId: familyIdParam,
          familyMember1Id: parentId,
          familyMember2Id: childId,
          relationshipType: linkPcRelationshipType
        });
        if (!result.success) {
          setSubmitError(
            result.error?.getUserMessage?.() ?? result.error?.message ?? t('addFlow.couldNotCreateRelationship')
          );
          return;
        }
      }

      await familyStore.loadFamilyData(familyIdParam, true);
      navigate(`/families/${familyIdParam}/people/${linkOtherMemberId}`, {
        state: locationState?.from
          ? { from: locationState.from, originalAnchorId: locationState.originalAnchorId }
          : undefined
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('addFlow.couldNotCompleteLink'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.givenName?.trim()) {
      newErrors.givenName = t('addFlow.givenNameRequired');
    }
    if (formData.birthDate && formData.deathDate) {
      const pb = parseDateOnly(formData.birthDate);
      const pd = parseDateOnly(formData.deathDate);
      if (pb && pd) {
        const birthCompare = pb.year * 10000 + pb.month * 100 + pb.day;
        const deathCompare = pd.year * 10000 + pd.month * 100 + pd.day;
        if (deathCompare < birthCompare) {
          newErrors.deathDate = t('addFlow.deathAfterBirth');
        }
      }
    }
    if (formData.deathDate?.trim() && !formData.isDeceased) {
      newErrors.deathDate = t('addFlow.markDeceasedForDeathDate');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setUpgradeHint(null);

    if (!familyIdParam) {
      setSubmitError(t('addFlow.missingFamily'));
      return;
    }
    if (!addSelf && !((parentFamilyMemberId && (isChild || isSpouse)) || (childFamilyMemberId && isParent))) {
      setSubmitError(t('addFlow.invalidParams'));
      return;
    }

    if (!addSelf && entryMode === 'link') {
      await submitLinkExisting();
      return;
    }

    if (!validate()) return;

    setIsSubmitting(true);
    const birthDateVal = formData.birthDate?.trim() || undefined;

    try {
      if (addSelf) {
        const request = {
          familyId: familyIdParam,
          givenName: formData.givenName.trim(),
          middleName: formData.middleName?.trim() || undefined,
          surname: formData.surname?.trim() || undefined,
          suffix: formData.suffix?.trim() || undefined,
          preferredName: formData.preferredName?.trim() || undefined,
          gender: formData.gender,
          birthDate: birthDateVal,
          birthDatePrecision: birthDateVal ? formData.birthDatePrecision : undefined,
          birthPlace: formData.birthPlace?.trim() || undefined,
          deathDate: formData.isDeceased ? formData.deathDate?.trim() || undefined : undefined,
          deathDatePrecision:
            formData.isDeceased && formData.deathDate?.trim() ? formData.deathDatePrecision : undefined,
          deathPlace: formData.isDeceased ? formData.deathPlace?.trim() || undefined : undefined,
          livingStatus: (formData.isDeceased ? 'deceased' : 'living') as LivingStatusType
        };
        const createResult = await familyStore.createPerson(request);
        if (!createResult.success) {
          const parts = getUpgradeAwareErrorParts(createResult.error, isOwner, t('addFlow.failedAddYourself'));
          setSubmitError(parts.message);
          setUpgradeHint(parts.upgradeHint);
          return;
        }
        if (!createResult.data) {
          setSubmitError(t('addFlow.failedAddYourself'));
          return;
        }
        const newFamilyMemberId = createResult.data.familyMemberId;
        const claimResult = await familyStore.claimProfile(newFamilyMemberId);
        if (!claimResult.success) {
          setSubmitError(claimResult.error?.getUserMessage?.() ?? t('addFlow.createdButClaimFailed'));
        }
        await familyStore.loadFamilyData(familyIdParam, true);
        navigate(`/families/${familyIdParam}/people/${newFamilyMemberId}`, { state: { from: 'add-self' } });
      } else {
        const selectedAdditionalParents = isChild
          ? spouseIds
              .filter((id) => additionalParentChoices[id]?.selected)
              .map((spouseFamilyMemberId) => ({
                parentFamilyMemberId: spouseFamilyMemberId,
                relationshipType:
                  additionalParentChoices[spouseFamilyMemberId]?.relationshipType ??
                  ParentChildRelationshipType.Biological
              }))
          : [];
        const request = {
          familyId: familyIdParam,
          ...(isParent ? { childFamilyMemberId: childFamilyMemberId } : { parentFamilyMemberId }),
          memberType: isParent
            ? AddFamilyMemberType.Parent
            : isSpouse
              ? AddFamilyMemberType.Spouse
              : AddFamilyMemberType.Child,
          relationshipType: isChild
            ? formData.parentChildRelationshipType
            : isParent
              ? formData.parentChildRelationshipType
              : undefined,
          ...(selectedAdditionalParents.length > 0 && { additionalParents: selectedAdditionalParents }),
          givenName: formData.givenName.trim(),
          middleName: formData.middleName?.trim() || undefined,
          surname: formData.surname?.trim() || undefined,
          suffix: formData.suffix?.trim() || undefined,
          preferredName: formData.preferredName?.trim() || undefined,
          gender: formData.gender,
          birthDate: birthDateVal,
          birthDatePrecision: birthDateVal ? formData.birthDatePrecision : undefined,
          birthPlace: formData.birthPlace?.trim() || undefined,
          deathDate: formData.isDeceased ? formData.deathDate?.trim() || undefined : undefined,
          deathDatePrecision:
            formData.isDeceased && formData.deathDate?.trim() ? formData.deathDatePrecision : undefined,
          deathPlace: formData.isDeceased ? formData.deathPlace?.trim() || undefined : undefined,
          livingStatus: (formData.isDeceased ? 'deceased' : 'living') as LivingStatusType
        };
        const result = await familyStore.addFamilyMember(request);
        if (result.success) {
          await familyStore.loadFamilyData(familyIdParam, true);
          navigate(`/families/${familyIdParam}/people/${result.data.familyMemberId}`, {
            state: locationState?.from
              ? { from: locationState.from, originalAnchorId: locationState.originalAnchorId }
              : undefined
          });
        } else {
          const parts = getUpgradeAwareErrorParts(result.error, isOwner, t('addFlow.failedAddMember'));
          setSubmitError(parts.message);
          setUpgradeHint(parts.upgradeHint);
        }
      }
    } catch (error) {
      setSubmitError(addSelf ? t('addFlow.failedAddYourself') : t('addFlow.failedAddMember'));
      // eslint-disable-next-line no-console
      console.error('Add family member error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleDeceasedChange = (deceased: boolean) => {
    setFormData((prev) => ({
      ...prev,
      isDeceased: deceased,
      ...(deceased ? {} : { deathDate: '', deathPlace: '' })
    }));
    if (errors.deathDate) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.deathDate;
        return next;
      });
    }
  };

  const handlePrecisionChange = (precision: DatePrecision) => {
    setFormData((prev) => {
      const birth = prev.birthDate?.trim() || '';
      let nextBirth = birth;
      if (birth) {
        const p = parseDateOnly(birth);
        if (p) {
          const m = String(p.month).padStart(2, '0');
          if (precision === 'year') nextBirth = `${p.year}-01-01`;
          else if (precision === 'month') nextBirth = `${p.year}-${m}-01`;
          else nextBirth = formatDateForInput(birth);
        }
      }
      return { ...prev, birthDatePrecision: precision, birthDate: nextBirth };
    });
  };

  const handleBirthDateChange = (rawValue: string) => {
    const canonical = toCanonicalBirthDate(rawValue, formData.birthDatePrecision);
    if (rawValue.trim() === '') return;
    if (canonical === '') return;
    setFormData((prev) => ({ ...prev, birthDate: canonical }));
    if (errors.birthDate) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.birthDate;
        return next;
      });
    }
  };

  const handleDeathPrecisionChange = (precision: DatePrecision) => {
    setFormData((prev) => {
      const death = prev.deathDate?.trim() || '';
      let nextDeath = death;
      if (death) {
        const p = parseDateOnly(death);
        if (p) {
          const m = String(p.month).padStart(2, '0');
          if (precision === 'year') nextDeath = `${p.year}-01-01`;
          else if (precision === 'month') nextDeath = `${p.year}-${m}-01`;
          else nextDeath = formatDateForInput(death);
        }
      }
      return { ...prev, deathDatePrecision: precision, deathDate: nextDeath };
    });
  };

  const handleDeathDateChange = (rawValue: string) => {
    const canonical = toCanonicalBirthDate(rawValue, formData.deathDatePrecision);
    if (rawValue.trim() === '') return;
    if (canonical === '') return;
    setFormData((prev) => ({ ...prev, deathDate: canonical }));
    if (errors.deathDate) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.deathDate;
        return next;
      });
    }
  };

  const handleAdditionalParentToggle = (spouseFamilyMemberId: string, selected: boolean) => {
    setAdditionalParentChoices((prev) => ({
      ...prev,
      [spouseFamilyMemberId]: {
        ...(prev[spouseFamilyMemberId] ?? {
          selected: false,
          relationshipType: ParentChildRelationshipType.Biological
        }),
        selected
      }
    }));
  };

  const handleAdditionalParentRelationshipType = (
    spouseFamilyMemberId: string,
    relationshipType: ParentChildRelationshipTypeValue
  ) => {
    setAdditionalParentChoices((prev) => ({
      ...prev,
      [spouseFamilyMemberId]: {
        ...(prev[spouseFamilyMemberId] ?? {
          selected: false,
          relationshipType: ParentChildRelationshipType.Biological
        }),
        relationshipType
      }
    }));
  };

  const handleCancel = () => {
    if (!familyIdParam) return;
    const treeAnchorFallback =
      locationState?.originalAnchorId ?? (anchorFamilyMemberId || familyStore.getRootPersonFamilyMemberId()) ?? 'first';
    navigateCancelFromAddFlow(navigate, {
      familyId: familyIdParam,
      locationState,
      treeAnchorFallback
    });
  };

  if (!familyIdParam) {
    return (
      <div className="add-member-page add-member-page--centered">
        <section className="add-member-page__section add-member-page__section--message">
          <h2 className="add-member-page__section-title">Missing family</h2>
          <p className="add-member-page__body">Please go back to your family and try again.</p>
          <button type="button" onClick={() => navigate('/families')} className="btn btn--primary">
            My families
          </button>
        </section>
      </div>
    );
  }

  if (!addSelf && !parentFamilyMemberId && !(relationshipType === 'parent' && childFamilyMemberId)) {
    return (
      <div className="add-member-page add-member-page--centered">
        <section className="add-member-page__section add-member-page__section--message">
          <h2 className="add-member-page__section-title">Missing parameters</h2>
          <p className="add-member-page__body">
            Go back to Members or Tree and use the + button on a person to add a child, spouse, or parent.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/families/${familyIdParam}/members`)}
            className="btn btn--primary"
          >
            {tForms('actions.backToMembers')}
          </button>
        </section>
      </div>
    );
  }

  if (!addSelf && !isChild && !isSpouse && !isParent) {
    return (
      <div className="add-member-page add-member-page--centered">
        <section className="add-member-page__section add-member-page__section--message">
          <h2 className="add-member-page__section-title">Invalid type</h2>
          <p className="add-member-page__body">Use the + button on a person to add a child, spouse, or parent.</p>
          <button type="button" onClick={handleCancel} className="btn btn--primary">
            {tForms('actions.backToMembers')}
          </button>
        </section>
      </div>
    );
  }

  const showCreateFields = addSelf || entryMode === 'create';
  const additionalParentSpouses = spouseIds.map((spouseFamilyMemberId) => {
    const spouse = familyStore.getPerson(spouseFamilyMemberId);
    return {
      spouseFamilyMemberId,
      displayName: spouse ? `${spouse.firstName} ${spouse.lastName}`.trim() || 'Unknown' : 'Unknown'
    };
  });
  const showAdditionalParents = entryMode === 'create' && isChild && additionalParentSpouses.length > 0;

  const submitLabel = getSubmitLabel(addSelf, entryMode, isSpouse, isSubmitting);
  const pageTitle = addSelf
    ? t('addYourself.title')
    : isParent
      ? t('addParent.title')
      : isSpouse
        ? t('addSpouse.title')
        : t('addChild.title');
  const pageLede = addSelf
    ? t('addYourself.lede')
    : isParent
      ? t('addParent.lede')
      : isSpouse
        ? t('addSpouse.lede')
        : t('addChild.lede');

  return (
    <div className="add-member-page">
      <header className="add-member-page__header">
        <h1 className="add-member-page__title">{pageTitle}</h1>
        <p className="add-member-page__lede">
          {pageLede} <ScreenHelpDot />
        </p>
      </header>

      <section className="add-member-page__section">
        <form onSubmit={handleSubmit} className="add-member-page__form person-edit-form">
          {!addSelf && (
            <ToggleGroup.Root
              type="single"
              value={entryMode}
              onValueChange={(v) => v && setEntryMode(v as 'create' | 'link')}
              className="add-flow-entry-toggle"
              aria-label={t('addFlow.howToAddPerson')}
            >
              <ToggleGroup.Item value="create" className="add-flow-entry-toggle__item">
                {t('addFlow.createNewPerson')}
              </ToggleGroup.Item>
              <ToggleGroup.Item value="link" className="add-flow-entry-toggle__item">
                {t('addFlow.linkExisting')}
              </ToggleGroup.Item>
            </ToggleGroup.Root>
          )}

          {!addSelf && entryMode === 'link' && (
            <AddFamilyMemberLinkExistingFields
              isChild={isChild}
              isParent={isParent}
              isSpouse={isSpouse}
              linkOtherMemberId={linkOtherMemberId}
              onLinkOtherMemberIdChange={setLinkOtherMemberId}
              linkPcRelationshipType={linkPcRelationshipType}
              onLinkPcRelationshipTypeChange={setLinkPcRelationshipType}
              members={linkMemberOptions}
              isSubmitting={isSubmitting}
            />
          )}

          {submitError && (
            <PlanUpgradeAlert
              message={submitError}
              upgradeHint={upgradeHint}
              onDismiss={() => {
                setSubmitError(null);
                setUpgradeHint(null);
              }}
            />
          )}

          {showCreateFields && (
            <AddFamilyMemberCreateNewFields
              showRelationshipToParent={!addSelf && (isChild || isParent)}
              formData={formData}
              errors={errors}
              isSubmitting={isSubmitting}
              onChange={handleChange}
              onPrecisionChange={handlePrecisionChange}
              onBirthDateChange={handleBirthDateChange}
              onDeathPrecisionChange={handleDeathPrecisionChange}
              onDeathDateChange={handleDeathDateChange}
              onDeceasedChange={handleDeceasedChange}
              additionalParents={
                showAdditionalParents
                  ? {
                      spouses: additionalParentSpouses,
                      choices: additionalParentChoices,
                      onToggle: handleAdditionalParentToggle,
                      onRelationshipType: handleAdditionalParentRelationshipType
                    }
                  : undefined
              }
            />
          )}

          <div className="person-edit-form__actions">
            <button type="button" onClick={handleCancel} disabled={isSubmitting} className="btn btn--ghost">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn btn--primary">
              {submitLabel}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
});

export default AddFamilyMemberScreen;
